import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ActivityIndicator,
  Platform,
  ScrollView,
  TouchableOpacity,
  Alert,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { ScreenLayout, Header, FormHeader, Input, Button, BusinessUserPicker, Dropdown } from '../components';
import type { BusinessUser } from '../services/api';
import { apiService } from '../services/api';
import { useAppContext } from '../contexts/AppContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { useAppStore } from '../store/appStore';
import { colors, spacing, typography } from '../theme';
import { designTokens } from '../theme/design-system';
import { useThemedStyles } from '../hooks/useThemedStyles';

type DepartmentType = 'operational' | 'support' | 'management' | 'sales' | 'marketing' | 'customer_service' | 'technical' | 'other';

interface DepartmentFormData {
  name: string;
  description?: string;
  type: DepartmentType;
  managerId?: string;
  isActive?: boolean;
}

interface AddDepartmentScreenProps {
  mode: 'new' | 'edit';
  departmentId?: string; // Required for 'edit' mode
}

/**
 * Unified Add/Edit Department Screen
 * 
 * Handles both creating new departments and editing existing departments
 * based on the `mode` prop:
 * - 'new': Creates a new department
 * - 'edit': Edits existing department (requires departmentId)
 */
export const AddDepartmentScreen: React.FC<AddDepartmentScreenProps> = ({
  mode,
  departmentId: propDepartmentId,
}) => {
  const styles = useThemedStyles(getStyles);
  const { navigateTo, showNotification, activeTab, setActiveTab, routeParams } = useAppContext();
  const { currentBusiness } = useAppStore();
  
  // Get departmentId from props, routeParams (web), or URL params
  const departmentId = propDepartmentId || 
    (routeParams as any)?.departmentId || 
    (Platform.OS === 'web' ? new URLSearchParams(window.location.search).get('departmentId') : null);
  const [loading, setLoading] = useState(false);
  const [loadingDepartment, setLoadingDepartment] = useState(false);
  const isLoadingDepartmentRef = useRef(false);

  const [formData, setFormData] = useState<DepartmentFormData>({
    name: '',
    description: '',
    type: 'operational',
    managerId: '',
    isActive: true,
  });
  
  const [typeDropdownOpen, setTypeDropdownOpen] = useState(false);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showUserPicker, setShowUserPicker] = useState(false);
  const [selectedManager, setSelectedManager] = useState<BusinessUser | null>(null);
  const [showDepartmentUsersPicker, setShowDepartmentUsersPicker] = useState(false);
  const [departmentUsers, setDepartmentUsers] = useState<BusinessUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Memoize excludeUserIds to prevent infinite loops in BusinessUserPicker
  // In new mode, exclude manager only (no existing users yet)
  // In edit mode, exclude both manager and existing department users
  const excludeUserIds = useMemo(() => {
    const userIds: string[] = [];
    // Only exclude existing department users in edit mode
    if (mode === 'edit') {
      userIds.push(...departmentUsers.map(u => u.userId));
    }
    if (formData.managerId) {
      userIds.push(formData.managerId);
    }
    return userIds;
  }, [departmentUsers, formData.managerId, mode]);

  // Load manager details function - memoized
  const loadManagerDetails = useCallback(async (managerId: string) => {
    if (!currentBusiness?._id) {
      console.log('⚠️ [AddDepartmentScreen] loadManagerDetails: No business ID');
      return;
    }
    
    if (!managerId) {
      console.log('⚠️ [AddDepartmentScreen] loadManagerDetails: No manager ID provided');
      setSelectedManager(null);
      return;
    }
    
    try {
      console.log('🔄 [AddDepartmentScreen] Loading manager details for managerId:', managerId);
      const response = await apiService.getBusinessUsers(currentBusiness._id);
      if (response.statusCode === 1000 && response.data) {
        // Convert both to strings for comparison to handle ObjectId vs string mismatches
        const managerIdStr = String(managerId).trim();
        console.log('🔍 [AddDepartmentScreen] Searching for manager with userId:', managerIdStr);
        console.log('🔍 [AddDepartmentScreen] Total business users:', response.data.length);
        
        const manager = response.data.find((bu: BusinessUser) => {
          // Handle userId as string or object (if populated)
          let buUserIdStr = '';
          if (typeof bu.userId === 'string') {
            buUserIdStr = bu.userId.trim();
          } else if (bu.userId && typeof bu.userId === 'object' && '_id' in bu.userId) {
            buUserIdStr = String((bu.userId as any)._id || '').trim();
          } else {
            buUserIdStr = String(bu.userId || '').trim();
          }
          
          // Also check user._id as fallback
          const userObjectId = bu.user?._id ? String(bu.user._id).trim() : '';
          
          const matches = buUserIdStr === managerIdStr || userObjectId === managerIdStr;
          
          if (matches) {
            console.log('✅ [AddDepartmentScreen] Match found!', {
              managerId: managerIdStr,
              buUserId: buUserIdStr,
              userObjectId: userObjectId,
              user: bu.user?.firstName + ' ' + bu.user?.lastName,
              rawUserId: bu.userId,
              userIdType: typeof bu.userId
            });
          }
          return matches;
        });
        
        if (manager) {
          console.log('✅ [AddDepartmentScreen] Manager found:', manager.user?.firstName, manager.user?.lastName);
          console.log('🔍 [AddDepartmentScreen] Setting selectedManager with userId:', manager.userId);
          setSelectedManager(manager);
          // Force a re-render check
          setTimeout(() => {
            console.log('🔍 [AddDepartmentScreen] After setSelectedManager - checking state in next tick');
          }, 0);
        } else {
          console.warn('⚠️ [AddDepartmentScreen] Manager not found in business users. ManagerId:', managerIdStr);
          console.warn('⚠️ [AddDepartmentScreen] Available userIds (first 5):', response.data.slice(0, 5).map((bu: BusinessUser) => {
            const userId = typeof bu.userId === 'string' ? bu.userId : (bu.userId && typeof bu.userId === 'object' && '_id' in bu.userId ? (bu.userId as any)._id : String(bu.userId || ''));
            return {
              userId: userId,
              userObjectId: bu.user?._id,
              type: typeof bu.userId,
              name: bu.user?.firstName + ' ' + bu.user?.lastName
            };
          }));
          setSelectedManager(null);
        }
      } else {
        console.error('❌ [AddDepartmentScreen] Failed to load business users for manager:', {
          statusCode: response.statusCode,
          errors: response.errors
        });
        setSelectedManager(null);
      }
    } catch (error) {
      console.error('❌ [AddDepartmentScreen] Error loading manager details:', error);
      setSelectedManager(null);
    }
  }, [currentBusiness?._id]);

  // Load department users function - memoized to prevent loops
  const loadDepartmentUsers = useCallback(async () => {
    if (!departmentId) {
      console.log('⚠️ [AddDepartmentScreen] loadDepartmentUsers skipped: No departmentId');
      return;
    }
    
    if (mode !== 'edit') {
      console.log('⚠️ [AddDepartmentScreen] loadDepartmentUsers skipped: Not in edit mode');
      return;
    }
    
    try {
      console.log('🔄 [AddDepartmentScreen] Loading department users for department:', departmentId);
      setLoadingUsers(true);
      
      let response;
      try {
        response = await apiService.getDepartmentUsers(departmentId);
      } catch (apiError: any) {
        console.error('❌ [AddDepartmentScreen] API call failed:', apiError);
        setDepartmentUsers([]);
        setLoadingUsers(false);
        return;
      }
      
      // Check if response exists
      if (!response) {
        console.error('❌ [AddDepartmentScreen] getDepartmentUsers returned undefined');
        setDepartmentUsers([]);
        setLoadingUsers(false);
        return;
      }
      
      console.log('📦 [AddDepartmentScreen] API response received:', {
        statusCode: response.statusCode,
        hasData: !!response.data,
        dataLength: Array.isArray(response.data) ? response.data.length : 'not array'
      });
      
      if (response.statusCode === 1000) {
        console.log('✅ [AddDepartmentScreen] Department users response:', response.data);
        // Handle empty array case (no users assigned yet)
        if (!response.data || (Array.isArray(response.data) && response.data.length === 0)) {
          console.log('ℹ️ [AddDepartmentScreen] No users assigned to department yet');
          setDepartmentUsers([]);
        } else if (response.data) {
          // Get business users to match with department users
          if (currentBusiness?._id) {
            const businessUsersResponse = await apiService.getBusinessUsers(currentBusiness._id);
            if (businessUsersResponse.statusCode === 1000 && businessUsersResponse.data) {
              // Match department users with business users
              const deptUserIds = response.data.map((du: any) => du.userId || du._id);
              console.log('🔍 [AddDepartmentScreen] Department user IDs:', deptUserIds);
              const matchedUsers = businessUsersResponse.data.filter((bu: BusinessUser) =>
                deptUserIds.includes(bu.userId)
              );
              console.log('✅ [AddDepartmentScreen] Matched users:', {
                count: matchedUsers.length,
                userIds: matchedUsers.map(u => u.userId),
                deptUserIds: deptUserIds
              });
              setDepartmentUsers(matchedUsers);
              console.log('✅ [AddDepartmentScreen] State updated with', matchedUsers.length, 'users');
            } else {
              console.error('❌ [AddDepartmentScreen] Failed to load business users:', {
                statusCode: businessUsersResponse.statusCode,
                errors: businessUsersResponse.errors
              });
              setDepartmentUsers([]);
            }
          } else {
            console.error('❌ [AddDepartmentScreen] No current business ID available');
            setDepartmentUsers([]);
          }
        }
      } else {
        // Handle non-success status codes
        console.warn('⚠️ [AddDepartmentScreen] Non-success status code:', {
          statusCode: response?.statusCode,
          errors: response?.errors,
          message: response?.messages?.[0],
          data: response?.data
        });
        // For any non-success response, set empty array (might be no users assigned yet)
        setDepartmentUsers([]);
      }
    } catch (error: any) {
      console.error('❌ [AddDepartmentScreen] Unexpected error loading department users:', {
        error,
        message: error?.message,
        stack: error?.stack,
        name: error?.name
      });
      // Set empty array on error to prevent UI issues
      setDepartmentUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  }, [departmentId, mode, currentBusiness?._id]);

  // Load department data for edit mode - memoized to prevent loops
  const handleCancel = useCallback(() => {
    if (Platform.OS === 'web') {
      window.history.back();
    } else {
      navigateTo('departments');
    }
  }, [navigateTo]);


  // Load department data for edit mode - only run once when mode/departmentId changes
  useEffect(() => {
    console.log('🔍 [AddDepartmentScreen] useEffect triggered:', { 
      mode, 
      departmentId, 
      propDepartmentId, 
      routeParams,
      hasDepartmentId: !!departmentId 
    });
    
    if (mode === 'edit' && !departmentId) {
      console.error('❌ [AddDepartmentScreen] Department ID is required for edit mode');
      showNotification('error', 'Error', 'Department ID is required');
      if (Platform.OS === 'web') {
        window.history.back();
      } else {
        navigateTo('departments');
      }
      return;
    }
    
    if (mode === 'edit' && departmentId) {
      // Reset ref when departmentId changes
      isLoadingDepartmentRef.current = false;
      
      let isMounted = true;
      
      const fetchDepartment = async () => {
        // Prevent multiple simultaneous loads
        if (isLoadingDepartmentRef.current) {
          return;
        }
        
        if (!departmentId) {
          setLoadingDepartment(false);
          return;
        }

        isLoadingDepartmentRef.current = true;
        
        try {
          setLoadingDepartment(true);
          console.log('🔍 [AddDepartmentScreen] Loading department:', departmentId);
          const response = await apiService.getDepartmentById(departmentId);

          if (!isMounted) {
            isLoadingDepartmentRef.current = false;
            setLoadingDepartment(false);
            return;
          }

          if (response.statusCode === 1000 && response.data) {
            const department = response.data;
            console.log('✅ [AddDepartmentScreen] Department loaded:', department.name);
            
            // Extract managerId - handle both string and populated object cases
            let managerId = '';
            if (department.managerId) {
              if (typeof department.managerId === 'string') {
                managerId = department.managerId;
              } else if (typeof department.managerId === 'object' && department.managerId._id) {
                // managerId is populated as an object, extract the _id
                managerId = String(department.managerId._id);
              } else if (typeof department.managerId === 'object' && department.managerId.id) {
                // Alternative: some populated objects use .id instead of ._id
                managerId = String(department.managerId.id);
              }
            }
            console.log('🔍 [AddDepartmentScreen] Department managerId (extracted):', managerId);
            console.log('🔍 [AddDepartmentScreen] Department managerId (raw):', department.managerId);
            
            setFormData({
              name: department.name || '',
              description: department.description || '',
              type: department.type || 'operational',
              managerId: managerId,
              isActive: department.isActive !== undefined ? department.isActive : true,
            });
            
            // Load manager details if managerId exists (await to ensure it completes)
            if (managerId && currentBusiness?._id) {
              console.log('🔄 [AddDepartmentScreen] Loading manager details for:', managerId);
              try {
                await loadManagerDetails(managerId);
                console.log('✅ [AddDepartmentScreen] Manager details loaded, selectedManager state should be set');
                // Force a small delay to ensure state update propagates
                setTimeout(() => {
                  console.log('🔍 [AddDepartmentScreen] State check - selectedManager:', selectedManager ? 'SET' : 'NULL');
                }, 100);
              } catch (err) {
                console.error('❌ [AddDepartmentScreen] Error loading manager details:', err);
                setSelectedManager(null);
              }
            } else {
              console.log('⚠️ [AddDepartmentScreen] No managerId or businessId, skipping manager load', {
                managerId: managerId,
                businessId: currentBusiness?._id
              });
              setSelectedManager(null);
            }
            
            // Load department users (await to ensure it completes)
            console.log('🔄 [AddDepartmentScreen] Loading department users...');
            try {
              await loadDepartmentUsers();
              console.log('✅ [AddDepartmentScreen] Department users loaded successfully');
              
              // Ensure manager is in department users list if manager exists
              if (managerId && currentBusiness?._id) {
                // Check if manager is already in the list
                setDepartmentUsers((prev) => {
                  const isManagerInUsers = prev.some(u => {
                    const uId = typeof u.userId === 'string' ? u.userId : (u.userId && typeof u.userId === 'object' && '_id' in u.userId ? String((u.userId as any)._id) : String(u.userId || ''));
                    return uId === managerId;
                  });
                  
                  if (!isManagerInUsers) {
                    console.log('🔄 [AddDepartmentScreen] Manager not in users list, fetching and adding...');
                    // Fetch manager user and add to list asynchronously
                    apiService.getBusinessUsers(currentBusiness._id).then((businessUsersResponse) => {
                      if (businessUsersResponse.statusCode === 1000 && businessUsersResponse.data) {
                        const managerUser = businessUsersResponse.data.find((bu: BusinessUser) => {
                          const buUserIdStr = typeof bu.userId === 'string' ? bu.userId : (bu.userId && typeof bu.userId === 'object' && '_id' in bu.userId ? String((bu.userId as any)._id) : String(bu.userId || ''));
                          return buUserIdStr === managerId || String(bu.user?._id || '') === managerId;
                        });
                        
                        if (managerUser) {
                          setDepartmentUsers((current) => {
                            const alreadyExists = current.some(u => {
                              const uId = typeof u.userId === 'string' ? u.userId : (u.userId && typeof u.userId === 'object' && '_id' in u.userId ? String((u.userId as any)._id) : String(u.userId || ''));
                              return uId === managerId;
                            });
                            if (!alreadyExists) {
                              console.log('✅ [AddDepartmentScreen] Manager added to department users');
                              return [...current, managerUser];
                            }
                            return current;
                          });
                        }
                      }
                    }).catch((err) => {
                      console.error('❌ [AddDepartmentScreen] Error fetching manager user:', err);
                    });
                  }
                  return prev;
                });
              }
            } catch (err) {
              console.error('❌ [AddDepartmentScreen] Error loading department users in fetchDepartment:', err);
            }
          } else {
            const errorMessage = response.errors?.[0]?.message || 'Failed to load department';
            showNotification('error', 'Error', errorMessage);
          }
        } catch (error: any) {
          if (!isMounted) {
            isLoadingDepartmentRef.current = false;
            setLoadingDepartment(false);
            return;
          }
          console.error('Error loading department:', error);
          showNotification('error', 'Error', error.message || 'Failed to load department');
        } finally {
          isLoadingDepartmentRef.current = false;
          if (isMounted) {
            setLoadingDepartment(false);
          }
        }
      };

      fetchDepartment();

      return () => {
        isMounted = false;
        isLoadingDepartmentRef.current = false;
      };
    } else {
      setLoadingDepartment(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, departmentId]);

  // Additional useEffect to ensure manager is loaded when formData.managerId changes
  useEffect(() => {
    if (mode === 'edit' && formData.managerId && currentBusiness?._id) {
      // Check if we need to load the manager (either not loaded, or managerId doesn't match)
      const needsLoad = !selectedManager || 
                       (selectedManager.userId !== formData.managerId && 
                        String(selectedManager.userId || '').trim() !== String(formData.managerId).trim());
      
      if (needsLoad) {
        console.log('🔄 [AddDepartmentScreen] formData.managerId changed or manager not loaded, loading manager:', formData.managerId);
        console.log('🔍 [AddDepartmentScreen] Current selectedManager:', selectedManager ? `userId: ${selectedManager.userId}` : 'null');
        loadManagerDetails(formData.managerId).catch((err) => {
          console.error('❌ [AddDepartmentScreen] Error loading manager in useEffect:', err);
        });
      } else {
        console.log('✅ [AddDepartmentScreen] Manager already loaded and matches formData.managerId');
      }
    } else if (mode === 'edit' && !formData.managerId) {
      // Clear manager if managerId is removed
      if (selectedManager) {
        console.log('🔄 [AddDepartmentScreen] managerId cleared, clearing selectedManager');
        setSelectedManager(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.managerId, mode, currentBusiness?._id]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Department name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) {
      Alert.alert('Validation Error', 'Please fix the errors in the form');
      return;
    }

    try {
      setLoading(true);

      if (mode === 'new') {
        if (!currentBusiness?._id) {
          showNotification('error', 'Error', 'Business ID is required');
          return;
        }

        const createData: any = {
          name: formData.name.trim(),
          description: formData.description?.trim() || undefined,
          businessId: currentBusiness._id,
          type: formData.type || 'operational',
          isActive: formData.isActive !== undefined ? formData.isActive : true,
        };
        
        // Only include managerId if it's not empty
        if (formData.managerId && formData.managerId.trim()) {
          createData.managerId = formData.managerId.trim();
          console.log('🔍 [AddDepartmentScreen] Including managerId in create:', createData.managerId);
        } else {
          console.log('🔍 [AddDepartmentScreen] No managerId to include. formData.managerId:', formData.managerId);
        }
        
        console.log('🔍 [AddDepartmentScreen] Creating department with data:', JSON.stringify(createData, null, 2));
        const response = await apiService.createDepartment(createData);

        if (response.statusCode === 1000 && response.data) {
          const newDepartmentId = response.data._id || response.data.id;
          console.log('✅ [AddDepartmentScreen] Department created with ID:', newDepartmentId);
          
          // Ensure manager is in department users if manager exists
          let usersToAdd = [...departmentUsers];
          if (formData.managerId && formData.managerId.trim() && currentBusiness?._id) {
            const managerId = formData.managerId.trim();
            const isManagerInUsers = usersToAdd.some(u => {
              const uId = typeof u.userId === 'string' ? u.userId : (u.userId && typeof u.userId === 'object' && '_id' in u.userId ? String((u.userId as any)._id) : String(u.userId || ''));
              return uId === managerId;
            });
            
            if (!isManagerInUsers) {
              console.log('🔄 [AddDepartmentScreen] Manager not in users list, fetching and adding for submission');
              // Always fetch manager user to ensure we have the correct data
              try {
                const businessUsersResponse = await apiService.getBusinessUsers(currentBusiness._id);
                if (businessUsersResponse.statusCode === 1000 && businessUsersResponse.data) {
                  const managerUser = businessUsersResponse.data.find((bu: BusinessUser) => {
                    const buUserIdStr = typeof bu.userId === 'string' ? bu.userId : (bu.userId && typeof bu.userId === 'object' && '_id' in bu.userId ? String((bu.userId as any)._id) : String(bu.userId || ''));
                    return buUserIdStr === managerId || String(bu.user?._id || '') === managerId;
                  });
                  if (managerUser) {
                    console.log('✅ [AddDepartmentScreen] Manager user found, adding to users list');
                    usersToAdd = [...usersToAdd, managerUser];
                  } else {
                    console.warn('⚠️ [AddDepartmentScreen] Manager user not found in business users');
                  }
                }
              } catch (err) {
                console.error('❌ [AddDepartmentScreen] Error fetching manager user:', err);
              }
            } else {
              console.log('✅ [AddDepartmentScreen] Manager already in users list');
            }
          }
          
          // If there are users to add, add them now
          if (usersToAdd.length > 0 && newDepartmentId && currentBusiness?._id) {
            console.log('🔄 [AddDepartmentScreen] Adding', usersToAdd.length, 'users to new department');
            let usersAdded = 0;
            let usersFailed = 0;
            
            for (const user of usersToAdd) {
              try {
                const addUserResponse = await apiService.addUserToDepartment(newDepartmentId, user.userId, currentBusiness._id);
                if (addUserResponse && addUserResponse.statusCode === 1000) {
                  usersAdded++;
                  console.log('✅ [AddDepartmentScreen] User added:', user.userId);
                } else {
                  usersFailed++;
                  console.error('❌ [AddDepartmentScreen] Failed to add user:', user.userId, addUserResponse);
                }
              } catch (err) {
                usersFailed++;
                console.error('❌ [AddDepartmentScreen] Error adding user to department:', user.userId, err);
              }
            }
            
            if (usersAdded > 0) {
              console.log('✅ [AddDepartmentScreen] Successfully added', usersAdded, 'users to department');
            }
            if (usersFailed > 0) {
              console.warn('⚠️ [AddDepartmentScreen] Failed to add', usersFailed, 'users to department');
            }
          } else {
            console.log('ℹ️ [AddDepartmentScreen] No users to add or missing departmentId/businessId');
          }
          
          showNotification('success', 'Success', 'Department created successfully.');
          setTimeout(() => {
            if (Platform.OS === 'web') {
              window.history.back();
            } else {
              navigateTo('departments');
            }
          }, 1500);
        } else {
          const errorMessage = response.errors?.[0]?.message || 'Failed to create department';
          showNotification('error', 'Error', errorMessage);
        }
      } else {
        // Edit mode
        if (!departmentId) {
          showNotification('error', 'Error', 'Department ID is required');
          return;
        }

        const updateData: any = {
          name: formData.name.trim(),
          description: formData.description?.trim() || undefined,
          type: formData.type || 'operational',
          isActive: formData.isActive !== undefined ? formData.isActive : true,
        };
        
        // Only include managerId if it's not empty, otherwise set to null to clear it
        if (formData.managerId && formData.managerId.trim()) {
          updateData.managerId = formData.managerId.trim();
          console.log('🔍 [AddDepartmentScreen] Including managerId in update:', updateData.managerId);
        } else {
          updateData.managerId = null; // Explicitly set to null to clear manager
          console.log('🔍 [AddDepartmentScreen] Setting managerId to null to clear manager');
        }
        
        console.log('🔍 [AddDepartmentScreen] Updating department with data:', JSON.stringify(updateData, null, 2));
        console.log('🔍 [AddDepartmentScreen] Current formData state:', JSON.stringify(formData, null, 2));
        const response = await apiService.updateDepartment(departmentId, updateData);

        if (response.statusCode === 1000 && response.data) {
          // Ensure manager is in department users if manager exists
          if (formData.managerId && formData.managerId.trim() && currentBusiness?._id && departmentId) {
            const managerId = formData.managerId.trim();
            const isManagerInUsers = departmentUsers.some(u => {
              const uId = typeof u.userId === 'string' ? u.userId : (u.userId && typeof u.userId === 'object' && '_id' in u.userId ? String((u.userId as any)._id) : String(u.userId || ''));
              return uId === managerId;
            });
            
            if (!isManagerInUsers) {
              console.log('🔄 [AddDepartmentScreen] Adding manager to department users after update');
              try {
                const addManagerResponse = await apiService.addUserToDepartment(departmentId, managerId, currentBusiness._id);
                if (addManagerResponse && addManagerResponse.statusCode === 1000) {
                  console.log('✅ [AddDepartmentScreen] Manager added to department users');
                } else {
                  console.warn('⚠️ [AddDepartmentScreen] Failed to add manager to department users');
                }
              } catch (err) {
                console.error('❌ [AddDepartmentScreen] Error adding manager to department users:', err);
              }
            }
          }
          
          showNotification('success', 'Success', 'Department updated successfully.');
          setTimeout(() => {
            if (Platform.OS === 'web') {
              window.history.back();
            } else {
              navigateTo('departments');
            }
          }, 1500);
        } else {
          const errorMessage = response.errors?.[0]?.message || 'Failed to update department';
          showNotification('error', 'Error', errorMessage);
        }
      }
    } catch (error: any) {
      console.error(`Error ${mode === 'new' ? 'creating' : 'updating'} department:`, error);
      const errorMessage = error.message || 
                          error.errors?.[0]?.message || 
                          `Failed to ${mode === 'new' ? 'create' : 'update'} department`;
      showNotification('error', 'Error', errorMessage);
    } finally {
      setLoading(false);
    }
  };


  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);


  const handleManagerSelect = useCallback(async (userIds: string[]) => {
    console.log('🔍 [AddDepartmentScreen] handleManagerSelect called with userIds:', userIds);
    if (userIds.length > 0) {
      const managerId = userIds[0];
      console.log('🔍 [AddDepartmentScreen] Setting managerId:', managerId);
      setFormData((prev) => {
        const updated = { ...prev, managerId };
        console.log('🔍 [AddDepartmentScreen] Updated formData:', updated);
        return updated;
      });
      // Load manager details to display
      if (currentBusiness?._id) {
        await loadManagerDetails(managerId);
        
        // Automatically add manager to department users if not already added
        // We need to fetch the business user to add to the list
        try {
          const businessUsersResponse = await apiService.getBusinessUsers(currentBusiness._id);
          if (businessUsersResponse.statusCode === 1000 && businessUsersResponse.data) {
            const managerUser = businessUsersResponse.data.find((bu: BusinessUser) => {
              const buUserIdStr = typeof bu.userId === 'string' ? bu.userId : (bu.userId && typeof bu.userId === 'object' && '_id' in bu.userId ? String((bu.userId as any)._id) : String(bu.userId || ''));
              return buUserIdStr === managerId || String(bu.user?._id || '') === managerId;
            });
            
            if (managerUser) {
              setDepartmentUsers((prev) => {
                const isAlreadyInUsers = prev.some(u => {
                  const uId = typeof u.userId === 'string' ? u.userId : (u.userId && typeof u.userId === 'object' && '_id' in u.userId ? String((u.userId as any)._id) : String(u.userId || ''));
                  return uId === managerId;
                });
                if (!isAlreadyInUsers) {
                  console.log('🔄 [AddDepartmentScreen] Adding manager to department users (local state)');
                  return [...prev, managerUser];
                }
                return prev;
              });
              
              // In edit mode, immediately add manager to department via API
              if (mode === 'edit' && departmentId) {
                console.log('🔄 [AddDepartmentScreen] Adding manager to department via API (edit mode)');
                try {
                  const addManagerResponse = await apiService.addUserToDepartment(departmentId, managerId, currentBusiness._id);
                  if (addManagerResponse && addManagerResponse.statusCode === 1000) {
                    console.log('✅ [AddDepartmentScreen] Manager added to department via API');
                    showNotification('success', 'Success', 'Manager added to department');
                  } else {
                    console.warn('⚠️ [AddDepartmentScreen] Failed to add manager to department via API:', addManagerResponse);
                    showNotification('warning', 'Warning', 'Manager selected but failed to add to department users');
                  }
                } catch (err) {
                  console.error('❌ [AddDepartmentScreen] Error adding manager to department via API:', err);
                  showNotification('error', 'Error', 'Failed to add manager to department');
                }
              }
            }
          }
        } catch (error) {
          console.error('❌ [AddDepartmentScreen] Error fetching manager user:', error);
        }
      }
    } else {
      setFormData((prev) => ({ ...prev, managerId: '' }));
      setSelectedManager(null);
    }
    setShowUserPicker(false);
  }, [currentBusiness?._id, loadManagerDetails, mode, departmentId]);

  const handleRemoveManager = () => {
    updateField('managerId', '');
    setSelectedManager(null);
  };


  const handleAddDepartmentUsers = async (userIds: string[]) => {
    if (!currentBusiness?._id) {
      showNotification('error', 'Error', 'Business ID is required');
      return;
    }
    
    // In new mode, we don't have departmentId yet, so we'll just add to local state
    // Users will be saved when the department is created
    if (mode === 'new') {
      console.log('🔄 [AddDepartmentScreen] Adding users in new mode (will save on create)');
      try {
        // Fetch business users to get full user objects
        const businessUsersResponse = await apiService.getBusinessUsers(currentBusiness._id);
        if (businessUsersResponse.statusCode === 1000 && businessUsersResponse.data) {
          const newUsers = businessUsersResponse.data.filter((bu: BusinessUser) =>
            userIds.includes(bu.userId)
          );
          
          // Add new users to existing list
          setDepartmentUsers((prev) => {
            const existingIds = new Set(prev.map(u => u.userId));
            const uniqueNewUsers = newUsers.filter((u: BusinessUser) => !existingIds.has(u.userId));
            const updated = [...prev, ...uniqueNewUsers];
            console.log('✅ [AddDepartmentScreen] Added', uniqueNewUsers.length, 'users to local state');
            return updated;
          });
          
          showNotification('success', 'Success', `${newUsers.length} user${newUsers.length > 1 ? 's' : ''} added`);
        }
      } catch (error: any) {
        console.error('❌ [AddDepartmentScreen] Error fetching users:', error);
        showNotification('error', 'Error', 'Failed to add users');
      } finally {
        setShowDepartmentUsersPicker(false);
      }
      return;
    }
    
    // Edit mode - need departmentId
    if (!departmentId) {
      showNotification('error', 'Error', 'Department ID is required');
      return;
    }

    try {
      setLoadingUsers(true);
      const addedUserIds: string[] = [];
      let successCount = 0;
      let errorCount = 0;

      // Filter out users that are already added
      const newUserIds = userIds.filter(userId => {
        const isAlreadyAdded = departmentUsers.some(du => du.userId === userId);
        return !isAlreadyAdded;
      });

      if (newUserIds.length === 0) {
        showNotification('info', 'Info', 'All selected users are already in this department');
        setLoadingUsers(false);
        setShowDepartmentUsersPicker(false);
        return;
      }

      // Add users to department
      for (const userId of newUserIds) {
        try {
          const response = await apiService.addUserToDepartment(departmentId, userId, currentBusiness._id);
          if (response && response.statusCode === 1000) {
            successCount++;
            addedUserIds.push(userId);
            console.log('✅ [AddDepartmentScreen] User added:', userId);
          } else {
            errorCount++;
            console.error('❌ [AddDepartmentScreen] Failed to add user:', userId, response);
          }
        } catch (err: any) {
          errorCount++;
          console.error('❌ [AddDepartmentScreen] Error adding user:', userId, err);
        }
      }

      if (successCount > 0) {
        showNotification('success', 'Success', `${successCount} user${successCount > 1 ? 's' : ''} added to department`);
        
        // Optimistically update state by fetching the newly added users
        if (currentBusiness?._id && addedUserIds.length > 0) {
          try {
            const businessUsersResponse = await apiService.getBusinessUsers(currentBusiness._id);
            if (businessUsersResponse.statusCode === 1000 && businessUsersResponse.data) {
              const newUsers = businessUsersResponse.data.filter((bu: BusinessUser) =>
                addedUserIds.includes(bu.userId)
              );
              
              // Add new users to existing list
              setDepartmentUsers((prev) => {
                const existingIds = new Set(prev.map(u => u.userId));
                const uniqueNewUsers = newUsers.filter((u: BusinessUser) => !existingIds.has(u.userId));
                const updated = [...prev, ...uniqueNewUsers];
                console.log('✅ [AddDepartmentScreen] Optimistically updated state:', {
                  previousCount: prev.length,
                  newUsersCount: uniqueNewUsers.length,
                  updatedCount: updated.length
                });
                return updated;
              });
            }
          } catch (err) {
            console.error('❌ [AddDepartmentScreen] Error fetching new users:', err);
          }
        }

        // Don't auto-reload - the optimistic update is sufficient
        // The users will be loaded when the screen is opened next time or when explicitly refreshed
        console.log('✅ [AddDepartmentScreen] Users added successfully, keeping optimistic update');
      }
      
      if (errorCount > 0) {
        showNotification('error', 'Error', `Failed to add ${errorCount} user${errorCount > 1 ? 's' : ''}`);
      }
    } catch (error: any) {
      console.error('❌ [AddDepartmentScreen] Error adding users to department:', error);
      showNotification('error', 'Error', error.message || 'Failed to add users to department');
    } finally {
      setLoadingUsers(false);
      setShowDepartmentUsersPicker(false);
    }
  };

  const handleRemoveDepartmentUser = async (userId: string) => {
    if (!departmentId) return;

    try {
      setLoadingUsers(true);
      const response = await apiService.removeUserFromDepartment(departmentId, userId);

      if (response.statusCode === 1000) {
        showNotification('success', 'Success', 'User removed from department');
        // Reload department users to update the list
        await loadDepartmentUsers();
        console.log('✅ [AddDepartmentScreen] Department users reloaded after removing user');
      } else {
        const errorMessage = response.errors?.[0]?.message || 'Failed to remove user from department';
        showNotification('error', 'Error', errorMessage);
      }
    } catch (error: any) {
      console.error('❌ [AddDepartmentScreen] Error removing user from department:', error);
      showNotification('error', 'Error', error.message || 'Failed to remove user from department');
    } finally {
      setLoadingUsers(false);
    }
  };

  const updateField = <K extends keyof DepartmentFormData>(
    field: K,
    value: DepartmentFormData[K]
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Show loading state for edit mode while fetching department
  if (mode === 'edit' && loadingDepartment) {
    return (
      <ScreenLayout
        header={
          <Header
            showNotifications={true}
            showMessages={true}
            onNotificationPress={() => navigateTo('notifications')}
            onMessagePress={() => navigateTo('chat')}
            showNavigation={Platform.OS === 'web'}
            activeTab={activeTab}
            onTabPress={handleTabPress}
          />
        }
        showBottomTabs={Platform.OS !== 'web'}
        onLeftPaneClose={() => {}}
        leftPaneContent="settings"
        onSettingsItemPress={async (itemId) => {
          const { setPendingSettingsItem } = await import('../utils/tempStorage');
          await setPendingSettingsItem(itemId);
          navigateTo('settings');
        }}
        onRightPaneClose={() => {}}
      >
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Text style={styles.loadingText}>Loading department...</Text>
        </View>
      </ScreenLayout>
    );
  }

  // Validate required props
  if (mode === 'edit' && !departmentId) {
    return (
      <ScreenLayout
        header={
          <Header
            showNotifications={true}
            showMessages={true}
            onNotificationPress={() => navigateTo('notifications')}
            onMessagePress={() => navigateTo('chat')}
            showNavigation={Platform.OS === 'web'}
            activeTab={activeTab}
            onTabPress={handleTabPress}
          />
        }
        showBottomTabs={Platform.OS !== 'web'}
      >
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>Department ID is required</Text>
        </View>
      </ScreenLayout>
    );
  }

  return (
    <ScreenLayout
      header={
        <Header
          showNotifications={true}
          showMessages={true}
          onNotificationPress={() => navigateTo('notifications')}
          onMessagePress={() => navigateTo('chat')}
          showNavigation={Platform.OS === 'web'}
          activeTab={activeTab}
          onTabPress={handleTabPress}
        />
      }
      showBottomTabs={Platform.OS !== 'web'}
      onLeftPaneClose={() => {}}
      leftPaneContent="settings"
      onSettingsItemPress={async (itemId) => {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
      onRightPaneClose={() => {}}
      formHeader={
        <FormHeader
          title={mode === 'edit' ? 'Edit Department' : 'New Department'}
          onBack={handleCancel}
          actions={[{ icon: 'checkmark', primary: true, onPress: handleSubmit, loading, disabled: loading }]}
        />
      }
    >
      <View style={styles.container}>
        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={[
            styles.contentContainer,
            Platform.OS === 'web' && styles.contentContainerWeb,
            Platform.OS !== 'web' && styles.contentContainerMobile
          ]}
          showsVerticalScrollIndicator={false}
          {...(Platform.OS === 'web' && typeDropdownOpen && {
            // @ts-ignore - web-specific prop
            style: [styles.scrollView, { overflow: 'visible' as any }],
          })}
        >
          {/* Department Information */}
          <View style={[
            styles.section,
            typeDropdownOpen && styles.sectionWithDropdown
          ]}>
            <Text style={styles.sectionTitle}>Department Information</Text>

            <View style={styles.inputContainer}>
              <Input
                label="Department Name *"
                value={formData.name}
                onChangeText={(text) => updateField('name', text)}
                placeholder="Enter department name"
                error={errors.name}
                maxLength={100}
              />
            </View>

            <View style={styles.inputContainer}>
              <Input
                label="Description"
                value={formData.description || ''}
                onChangeText={(text) => updateField('description', text)}
                placeholder="Enter description (optional)"
                multiline
                numberOfLines={3}
                maxLength={500}
              />
            </View>

            <View style={[
              styles.inputContainer,
              typeDropdownOpen && styles.inputContainerWithDropdown
            ]}>
              <Dropdown
                label="Department Type *"
                value={formData.type}
                options={[
                  { value: 'operational', label: 'Operational' },
                  { value: 'support', label: 'Support' },
                  { value: 'management', label: 'Management' },
                  { value: 'sales', label: 'Sales' },
                  { value: 'marketing', label: 'Marketing' },
                  { value: 'customer_service', label: 'Customer Service' },
                  { value: 'technical', label: 'Technical' },
                  { value: 'other', label: 'Other' },
                ]}
                isOpen={typeDropdownOpen}
                onToggle={() => setTypeDropdownOpen(!typeDropdownOpen)}
                onSelect={(value) => {
                  updateField('type', value as DepartmentType);
                  setTypeDropdownOpen(false);
                }}
                placeholder="Select department type"
                style={styles.typeDropdown}
                wrapperStyle={[
                  styles.typeDropdownWrapper,
                  typeDropdownOpen && styles.typeDropdownWrapperOpen
                ]}
              />
            </View>

            {/* Manager Selection */}
            <View style={styles.inputContainer}>
              <Text style={styles.inputLabel}>Department Manager (Optional)</Text>
              {selectedManager ? (
                <View style={styles.selectedManagerContainer}>
                  <TouchableOpacity
                    style={styles.managerInfo}
                    onPress={() => setShowUserPicker(true)}
                    activeOpacity={0.7}
                  >
                    <View style={styles.managerAvatar}>
                      <Text style={styles.managerAvatarText}>
                        {selectedManager.user?.firstName?.[0] || ''}{selectedManager.user?.lastName?.[0] || ''}
                      </Text>
                    </View>
                    <View style={styles.managerDetails}>
                      <Text style={styles.managerName}>
                        {selectedManager.user?.firstName} {selectedManager.user?.lastName}
                      </Text>
                      <Text style={styles.managerEmail}>{selectedManager.user?.email}</Text>
                    </View>
                  </TouchableOpacity>
                  <View style={styles.managerActions}>
                    <TouchableOpacity
                      style={styles.changeManagerButton}
                      onPress={() => setShowUserPicker(true)}
                      hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                    >
                      <Ionicons name="swap-horizontal-outline" size={20} color={colors.primary} />
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={styles.removeManagerButton}
                      onPress={handleRemoveManager}
                      hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                    >
                      <Ionicons name="close-circle" size={24} color={colors.error} />
                    </TouchableOpacity>
                  </View>
                </View>
              ) : (
                <TouchableOpacity
                  style={styles.selectManagerButton}
                  onPress={() => setShowUserPicker(true)}
                  activeOpacity={0.7}
                >
                  <Ionicons name="person-add-outline" size={20} color={colors.primary} />
                  <Text style={styles.selectManagerButtonText}>Select Manager</Text>
                </TouchableOpacity>
              )}
            </View>

            {/* Department Users - Show in both new and edit mode */}
            {(
              <View style={styles.inputContainer}>
                <View style={styles.sectionHeader}>
                  <Text style={styles.inputLabel}>Department Users</Text>
                  <TouchableOpacity
                    style={styles.addUsersButton}
                    onPress={() => setShowDepartmentUsersPicker(true)}
                    activeOpacity={0.7}
                    disabled={loadingUsers}
                  >
                    <Ionicons name="person-add" size={18} color={colors.primary} />
                    <Text style={styles.addUsersButtonText}>Add Users</Text>
                  </TouchableOpacity>
                </View>
                
                {loadingUsers && departmentUsers.length === 0 ? (
                  <View style={styles.loadingUsersContainer}>
                    <ActivityIndicator size="small" color={colors.primary} />
                    <Text style={styles.loadingUsersText}>Loading users...</Text>
                  </View>
                ) : departmentUsers.length === 0 ? (
                  <View style={styles.emptyUsersContainer}>
                    <Ionicons name="people-outline" size={32} color={colors.text.secondary} />
                    <Text style={styles.emptyUsersText}>No users in this department</Text>
                    <Text style={styles.emptyUsersSubtext}>Add users to assign them to this department</Text>
                  </View>
                ) : (
                  <View style={styles.usersList}>
                    {departmentUsers.map((user) => (
                      <View key={user.userId} style={styles.userItem}>
                        <View style={styles.userItemInfo}>
                          <View style={styles.userItemAvatar}>
                            <Text style={styles.userItemAvatarText}>
                              {user.user?.firstName?.[0] || ''}{user.user?.lastName?.[0] || ''}
                            </Text>
                          </View>
                          <View style={styles.userItemDetails}>
                            <Text style={styles.userItemName}>
                              {user.user?.firstName || ''} {user.user?.lastName || ''}
                            </Text>
                            {user.user?.email ? (
                              <Text style={styles.userItemEmail}>{user.user.email}</Text>
                            ) : null}
                          </View>
                        </View>
                        <TouchableOpacity
                          style={styles.removeUserButton}
                          onPress={() => handleRemoveDepartmentUser(user.userId)}
                          hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                          disabled={loadingUsers}
                        >
                          <Ionicons name="close-circle" size={20} color={colors.error} />
                        </TouchableOpacity>
                      </View>
                    ))}
                  </View>
                )}
              </View>
            )}

            {/* Active Status Toggle */}
            <View style={styles.inputContainer}>
              <Text style={styles.inputLabel}>Status</Text>
              <TouchableOpacity
                style={styles.toggleContainer}
                onPress={() => updateField('isActive', !formData.isActive)}
                activeOpacity={0.7}
              >
                <View style={styles.toggleContent}>
                  <View style={styles.toggleInfo}>
                    <Text style={styles.toggleLabel}>
                      {formData.isActive ? 'Active' : 'Inactive'}
                    </Text>
                    <Text style={styles.toggleDescription}>
                      {formData.isActive 
                        ? 'Department is active and visible' 
                        : 'Department is inactive and hidden'}
                    </Text>
                  </View>
                  <View style={[
                    styles.toggleSwitch,
                    formData.isActive && styles.toggleSwitchActive
                  ]}>
                    <View style={[
                      styles.toggleThumb,
                      formData.isActive && styles.toggleThumbActive
                    ]} />
                  </View>
                </View>
              </TouchableOpacity>
            </View>
          </View>
        </ScrollView>
      </View>

      {/* Business User Picker for Manager */}
      <BusinessUserPicker
        visible={showUserPicker}
        onClose={() => setShowUserPicker(false)}
        onConfirm={handleManagerSelect}
        mode="single"
        title="Select Department Manager"
        confirmText="Select"
        selectedUserIds={formData.managerId ? [formData.managerId] : []}
      />

      {/* Business User Picker for Department Users - Available in both new and edit mode */}
      <BusinessUserPicker
        visible={showDepartmentUsersPicker}
        onClose={() => setShowDepartmentUsersPicker(false)}
        onConfirm={handleAddDepartmentUsers}
        mode="multiple"
        title="Add Users to Department"
        confirmText="Add Users"
        excludeUserIds={excludeUserIds}
      />
    </ScreenLayout>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.lightGray,
    ...(Platform.OS === 'web' && {
      width: '100%',
      maxWidth: '100%',
    }),
  },
  scrollView: {
    flex: 1,
  },
  contentContainer: {
    padding: spacing.lg,
    paddingBottom: spacing.xl,
    ...(Platform.OS === 'web' && {
      zIndex: 0,
      position: 'relative' as any,
    }),
  },
  contentContainerWeb: {
    paddingBottom: spacing.sm,
  },
  contentContainerMobile: {
    paddingBottom: 100, // Extra padding for mobile to avoid bottom tabs overlay
  },
  section: {
    ...(Platform.OS === 'web' && {
      zIndex: 1,
      position: 'relative' as any,
      overflow: 'visible' as any,
    }),
    marginBottom: spacing.lg,
    padding: spacing.md,
    backgroundColor: colors.background.surface,
    borderRadius: designTokens.borderRadius.lg,
    ...(Platform.OS !== 'web' && {
      zIndex: 1,
    }),
  },
  sectionWithDropdown: {
    ...(Platform.OS === 'web' && {
      zIndex: 99999,
      position: 'relative' as any,
      overflow: 'visible' as any,
    }),
    ...(Platform.OS !== 'web' && {
      zIndex: 99999,
      elevation: 50,
    }),
  },
  sectionTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: '600',
    color: colors.textDark,
    marginBottom: spacing.md,
    fontFamily: typography.fontFamily.semibold,
  },
  inputContainer: {
    marginBottom: spacing.lg,
    ...(Platform.OS === 'web' && {
      position: 'relative' as any,
      zIndex: 1,
    }),
  },
  inputContainerWithDropdown: {
    ...(Platform.OS === 'web' && {
      zIndex: 99999,
      position: 'relative' as any,
      overflow: 'visible' as any,
    }),
    ...(Platform.OS !== 'web' && {
      zIndex: 99999,
      elevation: 50,
    }),
  },
  inputLabel: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.semibold,
    color: colors.text,
    marginBottom: spacing.xs,
    fontFamily: typography.fontFamily.medium,
  },
  toggleContainer: {
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: designTokens.borderRadius.md,
    padding: spacing.md,
    backgroundColor: colors.background.surface,
  },
  toggleContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  toggleInfo: {
    flex: 1,
  },
  toggleLabel: {
    fontSize: typography.fontSize.base,
    fontWeight: '600',
    color: colors.textDark,
    marginBottom: spacing.xs / 2,
    fontFamily: typography.fontFamily.semibold,
  },
  toggleDescription: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    fontFamily: typography.fontFamily.regular,
  },
  toggleSwitch: {
    width: 50,
    height: 30,
    borderRadius: 15,
    backgroundColor: colors.border,
    justifyContent: 'center',
    paddingHorizontal: 2,
  },
  toggleSwitchActive: {
    backgroundColor: colors.primary,
  },
  toggleThumb: {
    width: 26,
    height: 26,
    borderRadius: 13,
    backgroundColor: colors.background.surface,
    position: 'absolute',
    left: 2,
  },
  toggleThumbActive: {
    left: 22,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.xl,
  },
  loadingText: {
    marginTop: spacing.md,
    fontSize: typography.fontSize.base,
    color: colors.textSecondary,
    fontFamily: typography.fontFamily.regular,
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.xl,
  },
  errorText: {
    fontSize: typography.fontSize.base,
    color: colors.error,
    fontFamily: typography.fontFamily.regular,
  },
  selectedManagerContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: spacing.md,
    backgroundColor: colors.background.primary,
    borderRadius: designTokens.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
  },
  managerInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  managerAvatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: colors.primary + '20',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: spacing.sm,
  },
  managerAvatarText: {
    fontSize: typography.fontSize.sm,
    fontWeight: '600',
    color: colors.primary,
    fontFamily: typography.fontFamily.semibold,
  },
  managerDetails: {
    flex: 1,
  },
  managerName: {
    fontSize: typography.fontSize.base,
    fontWeight: '600',
    color: colors.text.primary,
    fontFamily: typography.fontFamily.semibold,
    marginBottom: spacing.xs / 2,
  },
  managerEmail: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.regular,
  },
  managerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  changeManagerButton: {
    padding: spacing.xs,
  },
  removeManagerButton: {
    padding: spacing.xs,
  },
  selectManagerButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: spacing.md,
    backgroundColor: colors.background.primary,
    borderRadius: designTokens.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
    borderStyle: 'dashed',
  },
  selectManagerButtonText: {
    marginLeft: spacing.sm,
    fontSize: typography.fontSize.base,
    color: colors.primary,
    fontFamily: typography.fontFamily.medium,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.md,
  },
  addUsersButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    backgroundColor: colors.primary + '10',
    borderRadius: designTokens.borderRadius.sm,
    gap: spacing.xs,
  },
  addUsersButtonText: {
    fontSize: typography.fontSize.sm,
    color: colors.primary,
    fontFamily: typography.fontFamily.medium,
  },
  loadingUsersContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: spacing.lg,
    gap: spacing.sm,
  },
  loadingUsersText: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.regular,
  },
  emptyUsersContainer: {
    alignItems: 'center',
    padding: spacing.lg,
    backgroundColor: colors.background.primary,
    borderRadius: designTokens.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
    borderStyle: 'dashed',
  },
  emptyUsersText: {
    marginTop: spacing.sm,
    fontSize: typography.fontSize.base,
    fontWeight: '600',
    color: colors.text.primary,
    fontFamily: typography.fontFamily.semibold,
  },
  emptyUsersSubtext: {
    marginTop: spacing.xs,
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.regular,
    textAlign: 'center',
  },
  usersList: {
    gap: spacing.sm,
  },
  userItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: spacing.md,
    backgroundColor: colors.background.primary,
    borderRadius: designTokens.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
  },
  userItemInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  userItemAvatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: colors.primary + '20',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: spacing.sm,
  },
  userItemAvatarText: {
    fontSize: typography.fontSize.sm,
    fontWeight: '600',
    color: colors.primary,
    fontFamily: typography.fontFamily.semibold,
  },
  userItemDetails: {
    flex: 1,
  },
  userItemName: {
    fontSize: typography.fontSize.base,
    fontWeight: '600',
    color: colors.text.primary,
    fontFamily: typography.fontFamily.semibold,
    marginBottom: spacing.xs / 2,
  },
  userItemEmail: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.regular,
  },
  removeUserButton: {
    padding: spacing.xs,
  },
  typeDropdown: {
    flex: 1,
  },
  typeDropdownWrapper: {
    ...(Platform.OS === 'web' && {
      zIndex: 10000,
      position: 'relative' as any,
      overflow: 'visible' as any,
    }),
    ...(Platform.OS !== 'web' && {
      elevation: 20,
    }),
  },
  typeDropdownWrapperOpen: {
    ...(Platform.OS === 'web' && {
      zIndex: 99999,
      position: 'relative' as any,
      overflow: 'visible' as any,
    }),
    ...(Platform.OS !== 'web' && {
      elevation: 50,
    }),
  },
});
